import React, { useEffect, useState, useCallback } from "react";
import client from "@/api/client";
import { useBranch } from "@/context/BranchContext";
import { useAuth } from "@/context/AuthContext";
import { rupiah, dateTimeFmt, todayISO, numberFmt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { downloadCSV } from "@/lib/export";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { TrendingUp, Wallet, ShoppingBag, Boxes, Download, Printer, Wallet2, HandHeart, UtensilsCrossed, Clock, Mail } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/api/client";

function Summary({ cards }) {
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {cards.map((c) => (
        <Card key={c.label} className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><c.icon className="h-4 w-4" /> {c.label}</div>
          <div className="mt-1 font-mono text-xl font-semibold">{c.value}</div>
        </Card>
      ))}
    </div>
  );
}

function ComingSoon({ title, desc }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-slate-50 py-20 text-center" data-testid="coming-soon">
      <Clock className="mb-3 h-10 w-10 text-slate-400" />
      <div className="font-heading text-lg font-semibold text-slate-700">{title}</div>
      <div className="mt-1 max-w-md text-sm text-muted-foreground">{desc}</div>
      <span className="mt-4 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">Segera Hadir</span>
    </div>
  );
}

export default function Reports() {
  const { effectiveBranch, isAllBranch, branches } = useBranch();
  const { user } = useAuth();
  const isOwner = user.role === "super_admin";
  const isFinance = user.role === "financial";
  const isRM = user.role === "admin";
  const canExtras = isOwner || isFinance || isRM;  // piutang, penjualan khusus, pesanan siap saji, coming soon
  const canRegions = isOwner || isFinance || isRM; // tab per region
  const hideCost = ["branch_head", "cashier"].includes(user.role);
  const canEmail = ["super_admin", "admin"].includes(user.role);
  const [sendingRecap, setSendingRecap] = useState(false);
  const sendRecap = async () => {
    setSendingRecap(true);
    try {
      const r = await client.post("/reports/send-recap-now");
      toast.success(`Rekap terkirim ke ${r.data.to}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSendingRecap(false); }
  };

  const [start, setStart] = useState(todayISO().slice(0, 8) + "01");
  const [end, setEnd] = useState(todayISO());
  const [regionId, setRegionId] = useState("");
  const [regions, setRegions] = useState([]);

  const [sales, setSales] = useState(null);
  const [purch, setPurch] = useState(null);
  const [inv, setInv] = useState([]);
  const [regionsData, setRegionsData] = useState([]);
  const [recv, setRecv] = useState([]);
  const [special, setSpecial] = useState(null);
  const [food, setFood] = useState(null);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "-";

  useEffect(() => {
    if (!isAllBranch) return;
    client.get("/price-regions").then((r) => setRegions(r.data)).catch(() => {});
  }, [isAllBranch]);

  const params = useCallback((withDates = true) => {
    const p = {};
    if (effectiveBranch) p.branch_id = effectiveBranch;
    if (regionId) p.region_id = regionId;
    if (withDates) { p.start = start; p.end = end; }
    return p;
    // eslint-disable-next-line
  }, [effectiveBranch, regionId, start, end]);

  const loadAll = useCallback(() => {
    client.get("/reports/sales", { params: params() }).then((r) => setSales(r.data)).catch(() => {});
    client.get("/reports/inventory", { params: params(false) }).then((r) => setInv(r.data)).catch(() => {});
    if (!hideCost) client.get("/reports/purchases", { params: params() }).then((r) => setPurch(r.data)).catch(() => {});
    if (canRegions) client.get("/reports/regions", { params: { ...(regionId ? { region_id: regionId } : {}), start, end } }).then((r) => setRegionsData(r.data)).catch(() => {});
    if (canExtras) {
      client.get("/receivables", { params: params(false) }).then((r) => setRecv(r.data)).catch(() => {});
      client.get("/special-sales", { params: params() }).then((r) => setSpecial(r.data)).catch(() => {});
      client.get("/reports/food-settlements", { params: params() }).then((r) => setFood(r.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [params, hideCost, canRegions, canExtras, start, end]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const setPreset = (p) => {
    const now = new Date();
    const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    if (p === "today") { setStart(iso(now)); setEnd(iso(now)); }
    else if (p === "yesterday") { const y = new Date(now); y.setDate(now.getDate() - 1); setStart(iso(y)); setEnd(iso(y)); }
    else if (p === "week") { const w = new Date(now); w.setDate(now.getDate() - 6); setStart(iso(w)); setEnd(iso(now)); }
    else if (p === "month") { setStart(iso(now).slice(0, 8) + "01"); setEnd(iso(now)); }
  };

  const invValue = inv.reduce((s, p) => s + (hideCost ? (p.sell_price || 0) : (p.avg_cost || 0)) * (p.stock_qty || 0), 0);

  const period = `${start}_${end}`;
  const exportSales = () => downloadCSV(`laporan-penjualan-${period}`,
    hideCost ? ["No", "Waktu", "Kasir", "Total"] : ["No", "Waktu", "Kasir", "Total", "HPP", "Laba"],
    (sales?.sales || []).map((s) => hideCost
      ? [s.number, s.created_at, s.cashier_name, Math.round(s.total)]
      : [s.number, s.created_at, s.cashier_name, Math.round(s.total), Math.round(s.cogs_total), Math.round(s.profit)]));
  const exportPurch = () => downloadCSV(`laporan-pembelian-${period}`,
    ["No", "Waktu", "Jumlah Item", "Total", "Oleh"],
    (purch?.purchases || []).map((p) => [p.number, p.created_at, p.items.length, Math.round(p.total), p.user_name]));
  const exportInv = () => downloadCSV(`laporan-inventori`,
    hideCost ? ["Produk", "Satuan", "Stok", "Harga Jual", "Nilai Stok"] : ["Produk", "Satuan", "Stok", "HPP", "Nilai Stok"],
    inv.map((p) => hideCost
      ? [p.name, p.base_unit, p.stock_qty, Math.round(p.sell_price || 0), Math.round((p.sell_price || 0) * (p.stock_qty || 0))]
      : [p.name, p.base_unit, p.stock_qty, Math.round(p.avg_cost || 0), Math.round((p.avg_cost || 0) * (p.stock_qty || 0))]));
  const exportRegions = () => downloadCSV(`laporan-region-${period}`,
    ["Region", "Jumlah Cabang", "Transaksi", "Omzet", "HPP", "Laba Kotor", "Retur", "Jual Khusus", "Laba Bersih"],
    regionsData.map((r) => [r.region, r.branches, r.count, Math.round(r.revenue), Math.round(r.cogs), Math.round(r.gross_profit), Math.round(r.returns), Math.round(r.special_loss || 0), Math.round(r.net_profit)]));
  const exportRecv = () => downloadCSV(`laporan-piutang`,
    ["Pelanggan", "Telepon", "Batas Kredit", "Total Kas Bon", "Dibayar", "Sisa Piutang"],
    recv.map((c) => [c.customer_name, c.phone, Math.round(c.credit_limit || 0), Math.round(c.total_credit), Math.round(c.total_paid), Math.round(c.outstanding)]));
  const exportSpecial = () => downloadCSV(`laporan-penjualan-khusus-${period}`,
    ["No", "Waktu", "Penerima", "Cabang", "Nilai HPP"],
    (special?.items || []).map((s) => [s.number, s.created_at, s.recipient_name, s.branch_name, Math.round(s.cogs_total || 0)]));
  const exportFood = () => downloadCSV(`laporan-pesanan-siap-saji-${period}`,
    ["No", "Waktu Setor", "Mitra", "Cabang", "Pendapatan"],
    (food?.orders || []).map((o) => [o.number, o.settled_at, o.reseller_name, branchName(o.branch_id), Math.round(o.settlement_amount || 0)]));

  const presetBtn = (key, label) => (
    <Button variant="outline" size="sm" onClick={() => setPreset(key)} data-testid={`preset-${key}`}>{label}</Button>
  );

  return (
    <div className="p-6 space-y-5 animate-in-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Laporan</h1>
          <p className="text-sm text-muted-foreground">
            {isFinance ? "Pelaporan keuangan — read-only per cabang / regional manager" : (hideCost ? "Penjualan, pendapatan & inventori" : "Penjualan, pembelian, inventori & HPP/laba")}
          </p>
        </div>
        {canEmail && <Button variant="outline" onClick={sendRecap} disabled={sendingRecap} data-testid="send-recap-btn"><Mail className="mr-2 h-4 w-4" /> Kirim Rekap Email</Button>}
      </div>

      {/* FILTER BAR */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          {canRegions && (
            <div className="space-y-1">
              <Label className="text-xs">Regional Manager</Label>
              <Select value={regionId || "all"} onValueChange={(v) => setRegionId(v === "all" ? "" : v)}>
                <SelectTrigger className="w-56" data-testid="report-region"><SelectValue placeholder="Semua Region" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Region</SelectItem>
                  {regions.map((r) => <SelectItem key={r.region_id} value={r.region_id} data-testid={`region-opt-${r.region_id}`}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1"><Label className="text-xs">Dari</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="report-start" /></div>
          <div className="space-y-1"><Label className="text-xs">Sampai</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="report-end" /></div>
          <div className="flex flex-wrap items-center gap-2">
            {presetBtn("today", "Hari Ini")}
            {presetBtn("yesterday", "Kemarin")}
            {presetBtn("week", "Minggu Ini")}
            {presetBtn("month", "Bulan Ini")}
            <Button onClick={loadAll} data-testid="apply-report-btn">Terapkan</Button>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="sales">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="sales" data-testid="tab-sales">{hideCost ? "Penjualan" : "Penjualan & Laba"}</TabsTrigger>
          {!hideCost && <TabsTrigger value="purchases" data-testid="tab-purchases">Pembelian</TabsTrigger>}
          <TabsTrigger value="inventory" data-testid="tab-inventory">Inventori</TabsTrigger>
          {canExtras && <TabsTrigger value="receivables" data-testid="tab-receivables">Piutang Pelanggan</TabsTrigger>}
          {canExtras && <TabsTrigger value="special" data-testid="tab-special">Penjualan Khusus</TabsTrigger>}
          {canExtras && <TabsTrigger value="food" data-testid="tab-food">Pesanan Siap Saji</TabsTrigger>}
          {canRegions && <TabsTrigger value="regions" data-testid="tab-regions">Per Region</TabsTrigger>}
          {canExtras && <TabsTrigger value="cashout" data-testid="tab-cashout">Tarik Tunai</TabsTrigger>}
          {canExtras && <TabsTrigger value="consign" data-testid="tab-consign">Barang Titipan %</TabsTrigger>}
        </TabsList>

        {/* PENJUALAN */}
        <TabsContent value="sales" className="mt-4 space-y-4">
          {sales && <Summary cards={hideCost ? [
            { label: "Transaksi", value: numberFmt(sales.count), icon: ShoppingBag },
            { label: "Pendapatan", value: rupiah(sales.revenue), icon: Wallet },
          ] : [
            { label: "Transaksi", value: numberFmt(sales.count), icon: ShoppingBag },
            { label: "Pendapatan", value: rupiah(sales.revenue), icon: Wallet },
            { label: "HPP", value: rupiah(sales.cogs), icon: Boxes },
            { label: "Laba Kotor", value: rupiah(sales.profit), icon: TrendingUp },
          ]} />}
          <div className="flex items-center justify-between gap-2">
            {!hideCost && sales && (sales.returns_count > 0 || sales.special_loss > 0) ? (
              <div className="text-sm text-muted-foreground">
                {sales.returns_count > 0 && <>Retur: <span className="font-mono text-orange-600">{sales.returns_count}× / {rupiah(sales.returns_refund)}</span>{" · "}</>}
                {sales.special_loss > 0 && <>Jual Khusus: <span className="font-mono text-rose-600">{sales.special_count}× / {rupiah(sales.special_loss)}</span>{" · "}</>}
                Laba Bersih: <span className="font-mono font-semibold text-emerald-600">{rupiah(sales.net_profit)}</span>
              </div>
            ) : <div />}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportSales} data-testid="export-sales-btn"><Download className="mr-2 h-4 w-4" /> Unduh Excel</Button>
              <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="mr-2 h-4 w-4" /> Cetak PDF</Button>
            </div>
          </div>
          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Waktu</TableHead><TableHead>Kasir</TableHead><TableHead className="text-right">Total</TableHead>{!hideCost && <TableHead className="text-right">HPP</TableHead>}{!hideCost && <TableHead className="text-right">Laba</TableHead>}</TableRow></TableHeader>
              <TableBody>
                {sales?.sales?.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-xs">{s.number}</TableCell>
                    <TableCell className="text-xs">{dateTimeFmt(s.created_at)}</TableCell>
                    <TableCell className="text-sm">{s.cashier_name}</TableCell>
                    <TableCell className="text-right font-mono">{rupiah(s.total)}</TableCell>
                    {!hideCost && <TableCell className="text-right font-mono text-muted-foreground">{rupiah(s.cogs_total)}</TableCell>}
                    {!hideCost && <TableCell className="text-right font-mono text-emerald-600">{rupiah(s.profit)}</TableCell>}
                  </TableRow>
                ))}
                {sales && !sales.sales.length && <TableRow><TableCell colSpan={hideCost ? 4 : 6} className="py-8 text-center text-muted-foreground">Tidak ada data pada periode ini</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* PEMBELIAN */}
        {!hideCost && (
        <TabsContent value="purchases" className="mt-4 space-y-4">
          {purch && <Summary cards={[
            { label: "Transaksi", value: numberFmt(purch.count), icon: ShoppingBag },
            { label: "Total Pembelian", value: rupiah(purch.total), icon: Wallet },
          ]} />}
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={exportPurch} data-testid="export-purch-btn"><Download className="mr-2 h-4 w-4" /> Unduh Excel</Button>
          </div>
          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Waktu</TableHead><TableHead>Item</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Oleh</TableHead></TableRow></TableHeader>
              <TableBody>
                {purch?.purchases?.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs">{p.number}</TableCell>
                    <TableCell className="text-xs">{dateTimeFmt(p.created_at)}</TableCell>
                    <TableCell className="text-sm">{p.items.length} item</TableCell>
                    <TableCell className="text-right font-mono">{rupiah(p.total)}</TableCell>
                    <TableCell className="text-sm">{p.user_name}</TableCell>
                  </TableRow>
                ))}
                {purch && !purch.purchases.length && <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">Tidak ada data</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        )}

        {/* INVENTORI */}
        <TabsContent value="inventory" className="mt-4 space-y-4">
          <Summary cards={[
            { label: "Jumlah Produk", value: numberFmt(inv.length), icon: Boxes },
            { label: hideCost ? "Total Nilai Stok (Harga Jual)" : "Total Nilai Stok", value: rupiah(invValue), icon: Wallet },
          ]} />
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={exportInv} data-testid="export-inv-btn"><Download className="mr-2 h-4 w-4" /> Unduh Excel</Button>
          </div>
          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>Produk</TableHead><TableHead>Satuan</TableHead><TableHead className="text-right">Stok</TableHead><TableHead className="text-right">{hideCost ? "Harga Jual" : "HPP"}</TableHead><TableHead className="text-right">Nilai</TableHead></TableRow></TableHeader>
              <TableBody>
                {inv.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell>{p.base_unit}</TableCell>
                    <TableCell className="text-right font-mono">{numberFmt(p.stock_qty)}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{rupiah(hideCost ? (p.sell_price || 0) : (p.avg_cost || 0))}</TableCell>
                    <TableCell className="text-right font-mono">{rupiah((hideCost ? (p.sell_price || 0) : (p.avg_cost || 0)) * (p.stock_qty || 0))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* PIUTANG PELANGGAN */}
        {canExtras && (
        <TabsContent value="receivables" className="mt-4 space-y-4" data-testid="receivables-report">
          <Summary cards={[
            { label: "Pelanggan Berpiutang", value: numberFmt(recv.length), icon: Wallet2 },
            { label: "Total Sisa Piutang", value: rupiah(recv.reduce((s, c) => s + c.outstanding, 0)), icon: Wallet },
          ]} />
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={exportRecv} data-testid="export-recv-btn"><Download className="mr-2 h-4 w-4" /> Unduh Excel</Button>
          </div>
          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>Pelanggan</TableHead><TableHead>Telepon</TableHead><TableHead className="text-right">Total Kas Bon</TableHead><TableHead className="text-right">Dibayar</TableHead><TableHead className="text-right">Sisa Piutang</TableHead></TableRow></TableHeader>
              <TableBody>
                {recv.map((c) => (
                  <TableRow key={c.customer_id} data-testid={`recv-row-${c.customer_id}`}>
                    <TableCell className="font-medium">{c.customer_name}</TableCell>
                    <TableCell className="text-sm">{c.phone || "-"}</TableCell>
                    <TableCell className="text-right font-mono">{rupiah(c.total_credit)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-600">{rupiah(c.total_paid)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-rose-600">{rupiah(c.outstanding)}</TableCell>
                  </TableRow>
                ))}
                {!recv.length && <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">Tidak ada piutang</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        )}

        {/* PENJUALAN KHUSUS */}
        {canExtras && (
        <TabsContent value="special" className="mt-4 space-y-4" data-testid="special-report">
          <Summary cards={[
            { label: "Transaksi", value: numberFmt(special?.count || 0), icon: HandHeart },
            { label: "Total Nilai (HPP)", value: rupiah(special?.total_loss || 0), icon: Wallet },
          ]} />
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={exportSpecial} data-testid="export-special-btn"><Download className="mr-2 h-4 w-4" /> Unduh Excel</Button>
          </div>
          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Waktu</TableHead><TableHead>Penerima</TableHead><TableHead>Cabang</TableHead><TableHead className="text-right">Nilai HPP</TableHead></TableRow></TableHeader>
              <TableBody>
                {special?.items?.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-xs">{s.number}</TableCell>
                    <TableCell className="text-xs">{dateTimeFmt(s.created_at)}</TableCell>
                    <TableCell className="text-sm">{s.recipient_name}</TableCell>
                    <TableCell className="text-sm">{s.branch_name}</TableCell>
                    <TableCell className="text-right font-mono text-rose-600">{rupiah(s.cogs_total || 0)}</TableCell>
                  </TableRow>
                ))}
                {special && !special.items.length && <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">Tidak ada penjualan khusus</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        )}

        {/* PESANAN SIAP SAJI (PENDAPATAN) */}
        {canExtras && (
        <TabsContent value="food" className="mt-4 space-y-4" data-testid="food-report">
          <Summary cards={[
            { label: "Pesanan Lunas", value: numberFmt(food?.count || 0), icon: UtensilsCrossed },
            { label: "Total Pendapatan", value: rupiah(food?.total || 0), icon: Wallet },
          ]} />
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={exportFood} data-testid="export-food-btn"><Download className="mr-2 h-4 w-4" /> Unduh Excel</Button>
          </div>
          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Waktu Setor</TableHead><TableHead>Mitra</TableHead><TableHead>Cabang</TableHead><TableHead className="text-right">Pendapatan</TableHead></TableRow></TableHeader>
              <TableBody>
                {food?.orders?.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell className="font-mono text-xs">{o.number}</TableCell>
                    <TableCell className="text-xs">{dateTimeFmt(o.settled_at)}</TableCell>
                    <TableCell className="text-sm">{o.reseller_name}</TableCell>
                    <TableCell className="text-sm">{branchName(o.branch_id)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-600">{rupiah(o.settlement_amount || 0)}</TableCell>
                  </TableRow>
                ))}
                {food && !food.orders.length && <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">Belum ada pesanan lunas pada periode ini</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        )}

        {/* PER REGION */}
        {canRegions && (
        <TabsContent value="regions" className="mt-4 space-y-4" data-testid="regions-report">
          <Summary cards={[
            { label: "Jumlah Region", value: numberFmt(regionsData.length), icon: Boxes },
            { label: "Total Omzet", value: rupiah(regionsData.reduce((s, r) => s + r.revenue, 0)), icon: Wallet },
            { label: "Total Laba Bersih", value: rupiah(regionsData.reduce((s, r) => s + r.net_profit, 0)), icon: TrendingUp },
          ]} />
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={exportRegions} data-testid="export-regions-btn"><Download className="mr-2 h-4 w-4" /> Unduh Excel</Button>
          </div>
          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>Region (Regional Manager)</TableHead><TableHead className="text-right">Cabang</TableHead><TableHead className="text-right">Transaksi</TableHead><TableHead className="text-right">Omzet</TableHead><TableHead className="text-right">HPP</TableHead><TableHead className="text-right">Laba Kotor</TableHead><TableHead className="text-right">Retur</TableHead><TableHead className="text-right">Jual Khusus</TableHead><TableHead className="text-right">Laba Bersih</TableHead></TableRow></TableHeader>
              <TableBody>
                {regionsData.map((r) => (
                  <TableRow key={r.manager_id} data-testid={`region-row-${r.manager_id}`}>
                    <TableCell className="font-medium">{r.region}</TableCell>
                    <TableCell className="text-right font-mono">{numberFmt(r.branches)}</TableCell>
                    <TableCell className="text-right font-mono">{numberFmt(r.count)}</TableCell>
                    <TableCell className="text-right font-mono">{rupiah(r.revenue)}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{rupiah(r.cogs)}</TableCell>
                    <TableCell className="text-right font-mono">{rupiah(r.gross_profit)}</TableCell>
                    <TableCell className="text-right font-mono text-orange-600">{rupiah(r.returns)}</TableCell>
                    <TableCell className="text-right font-mono text-rose-600">{rupiah(r.special_loss || 0)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-emerald-600">{rupiah(r.net_profit)}</TableCell>
                  </TableRow>
                ))}
                {!regionsData.length && <TableRow><TableCell colSpan={9} className="py-8 text-center text-muted-foreground">Tidak ada data pada periode ini</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        )}

        {/* COMING SOON: TARIK TUNAI */}
        {canExtras && (
        <TabsContent value="cashout" className="mt-4">
          <ComingSoon title="Laporan Tarik Tunai" desc="Rekap transaksi tarik tunai per cabang / regional manager akan tersedia di sini. Fitur ini sedang dalam pengembangan." />
        </TabsContent>
        )}

        {/* COMING SOON: PENDAPATAN % BARANG TITIPAN */}
        {canExtras && (
        <TabsContent value="consign" className="mt-4">
          <ComingSoon title="Pendapatan % dari Barang Titipan" desc="Perhitungan pendapatan komisi (persentase) dari barang titipan/konsinyasi akan tersedia di sini. Fitur ini sedang dalam pengembangan." />
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
